import React, { useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  SwapHoriz as SwapIcon,
  LocalOffer as SameFormulaIcon,
  Category as RelatedIcon,
  Close as CloseIcon,
  TrendingDown as CheaperIcon,
  TrendingUp as ExpensiveIcon,
} from "@material-ui/icons";
import { usePdvStyles } from "../styles/pdvStyles";
import clsx from "clsx";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatPercent = (value) => {
  if (value === 0) return "";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Math.round(value)}%`;
};

/**
 * Single suggestion item component
 */
const SuggestionItem = ({
  product,
  onAdd,
  onSwap,
  showPriceDiff = false,
  priceDifference = 0,
  priceDifferencePercent = 0,
  isSelected = false,
}) => {
  const classes = usePdvStyles();
  const isCheaper = priceDifference < 0;
  const isExpensive = priceDifference > 0;

  return (
    <Box
      className={clsx(classes.suggestionItem, {
        [classes.suggestionItemSelected]: isSelected,
      })}
    >
      <Box className={classes.suggestionItemInfo}>
        <Typography className={classes.suggestionItemName} title={product.name}>
          {product.name}
        </Typography>
        <Box className={classes.suggestionItemDetails}>
          <span className={classes.suggestionItemPrice}>
            {formatCurrency(product.price || product.salePrice)}
          </span>
          {showPriceDiff && priceDifferencePercent !== 0 && (
            <Chip
              size="small"
              className={clsx(classes.suggestionPriceDiff, {
                [classes.suggestionPriceCheaper]: isCheaper,
                [classes.suggestionPriceExpensive]: isExpensive,
              })}
              icon={isCheaper ? <CheaperIcon fontSize="small" /> : <ExpensiveIcon fontSize="small" />}
              label={formatPercent(priceDifferencePercent)}
            />
          )}
          {product.stockQuantity !== undefined && (
            <span className={classes.suggestionItemStock}>
              {product.stockQuantity} un
            </span>
          )}
        </Box>
      </Box>
      <Box className={classes.suggestionItemActions}>
        {onSwap && (
          <Tooltip title="Trocar pelo atual">
            <IconButton size="small" onClick={() => onSwap(product)} className={classes.suggestionSwapButton}>
              <SwapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Adicionar ao carrinho (Enter)">
          <IconButton size="small" onClick={() => onAdd(product)} color="primary">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/**
 * Main suggestions panel component
 */
const PdvSuggestionsPanel = ({
  sameFormula = { products: [], activePrinciple: null, enabled: false },
  relatedProducts = { products: [], enabled: false },
  loading = false,
  onAddProduct,
  onSwapProduct,
  onClose,
  selectedProduct = null,
  selectedIndex = -1,
  isActive = false,
}) => {
  const classes = usePdvStyles();

  const handleAdd = useCallback((product) => {
    if (onAddProduct) {
      onAddProduct(product);
    }
  }, [onAddProduct]);

  const handleSwap = useCallback((newProduct) => {
    if (onSwapProduct && selectedProduct) {
      onSwapProduct(selectedProduct, newProduct);
    }
  }, [onSwapProduct, selectedProduct]);

  const hasSameFormula = sameFormula.enabled && sameFormula.products.length > 0;
  const hasRelated = relatedProducts.enabled && relatedProducts.products.length > 0;
  const hasAnySuggestions = hasSameFormula || hasRelated;

  // Calculate global index for each product (sameFormula first, then related)
  const sameFormulaCount = hasSameFormula ? sameFormula.products.length : 0;

  // Don't render if no suggestions and not loading
  if (!loading && !hasAnySuggestions) {
    return null;
  }

  return (
    <Box
      className={clsx(classes.suggestionsPanel, {
        [classes.suggestionsPanelActive]: isActive,
      })}
    >
      {/* Header */}
      <Box className={classes.suggestionsPanelHeader}>
        <Typography variant="subtitle2" className={classes.suggestionsPanelTitle}>
          Sugestões
          {isActive && (
            <Typography variant="caption" component="span" style={{ marginLeft: 8, opacity: 0.7 }}>
              (Tab para navegar)
            </Typography>
          )}
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Loading state */}
      {loading && (
        <Box className={classes.suggestionsPanelLoading}>
          <CircularProgress size={24} />
          <Typography variant="caption">Buscando sugestões...</Typography>
        </Box>
      )}

      {/* Same Formula Section */}
      {!loading && hasSameFormula && (
        <Box className={classes.suggestionSection}>
          <Box className={classes.suggestionSectionHeader}>
            <SameFormulaIcon fontSize="small" className={classes.suggestionSectionIcon} />
            <Typography variant="caption" className={classes.suggestionSectionTitle}>
              Mesma Fórmula
            </Typography>
            {sameFormula.activePrinciple && (
              <Chip
                size="small"
                label={sameFormula.activePrinciple}
                variant="outlined"
                className={classes.activePrincipleChip}
              />
            )}
          </Box>
          <Box className={classes.suggestionsList}>
            {sameFormula.products.map((product, index) => (
              <SuggestionItem
                key={product.id}
                product={product}
                onAdd={handleAdd}
                onSwap={selectedProduct ? handleSwap : null}
                showPriceDiff={true}
                priceDifference={product.priceDifference}
                priceDifferencePercent={product.priceDifferencePercent}
                isSelected={isActive && selectedIndex === index}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Related Products Section */}
      {!loading && hasRelated && (
        <Box className={classes.suggestionSection}>
          <Box className={classes.suggestionSectionHeader}>
            <RelatedIcon fontSize="small" className={classes.suggestionSectionIcon} />
            <Typography variant="caption" className={classes.suggestionSectionTitle}>
              Produtos Relacionados
            </Typography>
          </Box>
          <Box className={classes.suggestionsList}>
            {relatedProducts.products.map((product, index) => (
              <SuggestionItem
                key={product.id}
                product={product}
                onAdd={handleAdd}
                showPriceDiff={false}
                isSelected={isActive && selectedIndex === (sameFormulaCount + index)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Empty state after loading */}
      {!loading && !hasAnySuggestions && (
        <Box className={classes.suggestionsEmpty}>
          <Typography variant="caption" color="textSecondary">
            Nenhuma sugestão disponível
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PdvSuggestionsPanel);
